import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import AppShell from "@/layout/AppShell";
import ActivityPage from "@/pages/ActivityPage";
import ExportPage from "@/pages/ExportPage";
import InboxPage from "@/pages/InboxPage";
import LoginPage from "@/pages/LoginPage";
import ManualExtractPage from "@/pages/ManualExtractPage";
import QueuePage from "@/pages/QueuePage";
import SettingsPage from "@/pages/SettingsPage";
import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { AuthCallback } from "@/auth/AuthCallback";
import { RequireAdmin } from "@/auth/RequireAdmin";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { BrandIcon } from "@/components/BrandLogo";
import { BrandToaster } from "@/components/BrandToaster";

const BootSplash = () => (
  <div
    className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background"
    data-testid="boot-splash"
  >
    <BrandIcon size="h-12 w-12" />
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading your workspace…
    </p>
  </div>
);

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Process a fresh sign-in BEFORE anything else (synchronous, no useEffect race).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (loading) return <BootSplash />;
  if (!user) return <LoginPage />;

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<InboxPage />} />
        <Route path="/queue" element={<QueuePage />} />
        <Route
          path="/extract"
          element={
            <RequireAdmin what="Manual extract">
              <ManualExtractPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/export"
          element={
            <RequireAdmin what="Export">
              <ExportPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/activity"
          element={
            <RequireAdmin what="The activity log">
              <ActivityPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAdmin what="Settings">
              <SettingsPage />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <BrandToaster />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
